// Demo tenant service — fully dynamic, fetches from POC API
// No hardcoded tenant lists. All data comes from live databases.
// DB naming: axoix_demo-{vertical}-{N} on MySQL

export interface DemoTenant {
  slug: string;
  companyName: string;
  subdomain: string;
  email: string;
  dashboardUrl: string;
  storefrontUrl: string | null;
  description: string;
}

export interface VerticalDemos {
  verticalSlug: string;
  verticalName: string;
  demos: DemoTenant[];
}

export const ADMIN_PASSWORD = "Demo@2026!";
export const CUSTOMER_PASSWORD = "Customer@123";

// POC API base URL for demo routing
const DEMO_API_BASE = "https://poc.axoix.io/api/v1/auth";

// Marketing site slug → DB vertical name mapping
const MARKETING_TO_DB: Record<string, string> = {
  "tourism": "travel",
  "cardealer": "car_dealer",
  "bikedealer": "bike_dealer",
  "salonspa": "salon",
  "ecommerce": "retail",
  "it_services": "services",
  "consulting": "services",
  "gym-fitness": "gym",
  "arena-gaming": "arena",
  "real-estate": "realestate",
  "car-dealer": "car_dealer",
  "bike-dealer": "bike_dealer",
  "it-services": "services",
  "study-abroad": "study_abroad",
  "salon-spa": "salon",
  "retail": "retail",
};

// Marketing slug → display name
const VERTICAL_NAMES: Record<string, string> = {
  hotel: "Hotel & Hospitality",
  restaurant: "Restaurant & F&B",
  tourism: "Travel & Tourism",
  travel: "Travel & Tourism",
  education: "Education & Training",
  healthcare: "Healthcare & Clinics",
  manufacturing: "Manufacturing",
  construction: "Construction",
  logistics: "Logistics & Supply Chain",
  trading: "Trading & Distribution",
  "real-estate": "Real Estate",
  realestate: "Real Estate",
  "car-dealer": "Car Dealership",
  cardealer: "Car Dealership",
  car_dealer: "Car Dealership",
  "bike-dealer": "Bike Dealership",
  bikedealer: "Bike Dealership",
  bike_dealer: "Bike Dealership",
  "salon-spa": "Salon & Spa",
  salonspa: "Salon & Spa",
  salon: "Salon & Spa",
  "gym-fitness": "Gym & Fitness",
  gym: "Gym & Fitness",
  "arena-gaming": "Arena & Sports",
  arena: "Arena & Sports",
  "study-abroad": "Study Abroad & Immigration",
  study_abroad: "Study Abroad & Immigration",
  consulting: "Consulting & Advisory",
  "it-services": "IT Services & Software",
  it_services: "IT Services & Software",
  services: "Professional Services",
  retail: "Retail & E-Commerce",
  ecommerce: "Retail & E-Commerce",
  sports: "Sports & Arena",
};

/**
 * Pseudo-verticals — marketing slugs with no vertical of their own in the demo
 * API, served by filtering a parent vertical's tenants.
 *
 * PG / hostel / co-living is a property TYPE inside the `hotel` vertical
 * (poc.axoix.io: PropertyType.PG / CO_LIVING / HOSTEL), so its demo tenants live
 * in `axoix_demo-hotel-*` databases alongside real hotels. Demo discovery is
 * DB-name driven (auth.py scans `SHOW DATABASES LIKE 'axoix_demo-%'`), so there
 * is no `pg` vertical to query — we filter the hotel list instead.
 *
 * `match` mirrors isPGPropertyType in poc.axoix.io/web/src/utils/propertyMix.js
 * so both sides classify a property identically. `allow` pins tenants whose name
 * wouldn't match on its own. A new PG-named demo is picked up with no code change.
 */
interface PseudoVertical {
  dbVertical: string;
  match: RegExp;
  allow?: string[];
  name: string;
}

const PSEUDO_VERTICALS: Record<string, PseudoVertical> = {
  pg: {
    dbVertical: "hotel",
    match: /(^|[\s_-])(pg|pgs|hostel|hostels|co-?living|paying\s*guests?)([\s_-]|$)/i,
    allow: ["demo-hotel-4"], // SafeStay Ladies PG
    name: "PG, Hostel & Co-living",
  },
};

/** Does a demo tenant belong to this pseudo-vertical? */
const matchesPseudo = (
  pseudo: PseudoVertical,
  tenant: { subdomain?: string; companyName?: string }
): boolean => {
  const subdomain = tenant.subdomain || "";
  if (pseudo.allow && pseudo.allow.includes(subdomain)) return true;
  return pseudo.match.test(tenant.companyName || "") || pseudo.match.test(subdomain);
};

/** Resolve marketing slug to the DB vertical used by the API */
const resolveDbVertical = (marketingSlug: string): string => {
  const pseudo = PSEUDO_VERTICALS[marketingSlug];
  if (pseudo) return pseudo.dbVertical;
  return MARKETING_TO_DB[marketingSlug] || marketingSlug;
};

/** Format subdomain into readable name: demo-hotel-3 → "Hotel Demo 3" */
const formatTenantName = (subdomain: string): string => {
  const parts = subdomain.replace("demo-", "").split("-");
  const num = parts.pop() || "";
  const vertical = parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
  return `${vertical} Demo ${num}`;
};

/** Get the least-loaded demo tenant for a vertical (smart routing) */
export const getDemoRoute = async (vertical: string): Promise<{
  subdomain: string;
  dashboardUrl: string;
  freeSlots: number;
  totalSlots: number;
  inUse: number;
} | null> => {
  const pseudo = PSEUDO_VERTICALS[vertical];
  if (pseudo) {
    // /demo-route/ load-balances across the WHOLE parent vertical and would hand
    // back a hotel. Pick the least-loaded matching tenant ourselves instead.
    const data = await getDemoAvailability(vertical);
    if (!data || data.tenants.length === 0) return null;
    const best = [...data.tenants].sort((a, b) => b.freeSlots - a.freeSlots)[0];
    return {
      subdomain: best.subdomain,
      dashboardUrl: `https://${best.subdomain}.axoix.io/login`,
      freeSlots: best.freeSlots,
      totalSlots: best.totalSlots,
      inUse: best.inUse,
    };
  }

  const dbVertical = resolveDbVertical(vertical);
  try {
    const res = await fetch(`${DEMO_API_BASE}/demo-route/${dbVertical}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/** Get real-time availability for all demo tenants of a vertical */
export const getDemoAvailability = async (vertical: string): Promise<{
  vertical: string;
  tenants: Array<{
    subdomain: string;
    instance: number;
    freeSlots: number;
    totalSlots: number;
    inUse: number;
    error?: boolean;
  }>;
  totalFree: number;
  totalInUse: number;
  totalCapacity: number;
} | null> => {
  const dbVertical = resolveDbVertical(vertical);
  try {
    const res = await fetch(`${DEMO_API_BASE}/demo-availability/${dbVertical}`);
    if (!res.ok) return null;
    const data = await res.json();

    const pseudo = PSEUDO_VERTICALS[vertical];
    if (!pseudo) return data;

    // Filter the parent vertical down, then recompute the totals — the API's
    // totals cover every tenant in the parent vertical, not just ours.
    const tenants = (data.tenants || []).filter((t: any) => matchesPseudo(pseudo, t));
    if (tenants.length === 0) return null;
    return {
      ...data,
      vertical,
      tenants,
      totalFree: tenants.reduce((sum: number, t: any) => sum + (t.freeSlots || 0), 0),
      totalInUse: tenants.reduce((sum: number, t: any) => sum + (t.inUse || 0), 0),
      totalCapacity: tenants.reduce((sum: number, t: any) => sum + (t.totalSlots || 0), 0),
    };
  } catch {
    return null;
  }
};

/** Fetch all available demo verticals and tenant counts from the API */
export const fetchDemoVerticals = async (): Promise<{
  verticals: Array<{ vertical: string; tenantCount: number; tenants: string[] }>;
  totalTenants: number;
} | null> => {
  try {
    const res = await fetch(`${DEMO_API_BASE}/demo-verticals`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

/**
 * Fetch demo tenants for a vertical from the live API.
 * Returns VerticalDemos with real tenant data, or null if not available.
 */
export const getDemosByVertical = async (slug: string): Promise<VerticalDemos | null> => {
  const pseudo = PSEUDO_VERTICALS[slug];
  const dbVertical = resolveDbVertical(slug);
  const verticalName =
    pseudo?.name || VERTICAL_NAMES[slug] || VERTICAL_NAMES[dbVertical] || slug;

  try {
    // Pass the original slug, not the resolved DB vertical — getDemoAvailability
    // resolves it again internally, and pseudo-verticals need the slug to filter.
    const data = await getDemoAvailability(slug);
    if (!data || !data.tenants || data.tenants.length === 0) return null;

    const demos: DemoTenant[] = data.tenants.map((t: any) => ({
      slug: t.subdomain,
      companyName: t.companyName || formatTenantName(t.subdomain),
      subdomain: t.subdomain,
      email: `admin@${t.subdomain}.axoix.io`,
      dashboardUrl: `https://${t.subdomain}.axoix.io/login`,
      storefrontUrl: `https://${t.subdomain}.axoix.io/s/${t.subdomain}`,
      description: `${t.companyName || verticalName} — ${t.freeSlots}/${t.totalSlots} slots free`,
    }));

    return {
      verticalSlug: slug,
      verticalName,
      demos,
    };
  } catch {
    return null;
  }
};

/** Get storefront URL for a subdomain */
export const getStorefrontUrl = (subdomain: string): string => {
  return `https://${subdomain}.axoix.io/s/${subdomain}`;
};
